import React, { useCallback } from "react";
import styles from "Components/NewOrder/index.module.scss";
import { useFormikContext } from "formik";
import { AppContext } from "Context/AppContext";
import { useEffect, useContext, useMemo, useRef } from "react";
import { useOrderAttributeProvider } from "../context/order";
import { calcuateSummary, calculateKintsugiItemPrice, houseAccountTaxCalculation, orderTaxSignature } from "Context/OrderContext/OrderCalculation";
import DiscountModal from "./OrderDiscount/OrderDiscount";
import TaxModal from "./OrderTax/OrderTax";
import { useState } from "react";
import { getEstimateTax } from "api/order";
import { orderObject } from "../utility/createOrderObject";
import { useUser } from "Context/UserContext/useUser";
import moment from "moment";
import { checkPriceDefference } from "../utility/util";
import { ALLOW_KINTSUGI_TAX_DATE } from "Helpers/Constants";
import DeliveryAndService from "./DeliveryAndService";
import UpchargeModel from "Components/Modal/UpchargeUpdate/upcharge-update";
import Tax from "Components/Modal/Tax/tax";

// Wrike 4584120693: an edit that leaves these alone keeps the checkout tax.
const ORDER_TAX = ["tax_amount", "tax_rate", "taxable_amount", "is_tax_calculated_manually"];
const LINE_TAX = ["tax", "tax_rate", "taxable_amount", "kintsugi_item_price"];
const pick = (o, keys) => Object.fromEntries(keys.map((k) => [k, o?.[k]]));
const taxLines = (v) => v.recipients.flatMap((r) => [...r.products, ...r.addons]);

const TotalSummary = (props) => {
  const formik = useFormikContext();
  const {
    summary,
    setSummary,
    module,
    orderDetail,
    active,
    isQuickSendOrder,
    setEstimating,
  } = useOrderAttributeProvider();
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [showServiceModel, setShowServiceModel] = useState(false);
  const [showUpcharge, setShowUpcharge] = useState(false);
  const [showTaxModal, setShowTaxModal] = useState(false);
  const [showTax, setShowTax] = useState(false);
  const { setIsLoading, showToastMessage } = useContext(AppContext);
  const { userState, reLogin } = useUser();
  // Signature of the last estimate request actually sent. taxCalculation is wired to
  // re-run whenever its summary/tax inputs change, and it writes tax_amount back into
  // those inputs — so an unstable input (e.g. a flapping line total) can re-trigger it
  // in a tight loop and hammer the Kintsugi tax API. Skip the call when the request is
  // byte-for-byte identical to the previous one (same request => same tax).
  const lastEstimateReq = useRef(null);
  const getUser = async () => {
    if (userState?.userProfile?.id) {
      return userState?.userProfile?.id;
    } else {
      const user = await reLogin();
      return user.id;
    }
  };
  // Concurrent estimates overlap while the CSR types; the save unblocks only when the last one settles.
  const inflight = useRef(0);
  // The order's own tax block, restored when the basis returns to it (qty change then revert).
  const savedTax = useRef(null);
  const restoreTax = ({ order, lines }) => {
    const put = (p) => ({ ...p, ...lines[p.product_key] });
    formik.setValues((v) => ({
      ...v,
      ...order,
      recipients: v.recipients.map((r) => ({ ...r, products: r.products.map(put), addons: r.addons.map(put) })),
    }));
  };

  const taxCalculation = useCallback(async () => {
    try {
      // For house account payment type, we use a different tax calculation method
      if (formik.values.payment_type === "house_account" && formik.values.tax_amount > 0) {
        lastEstimateReq.current = null;
        const summary = calcuateSummary(formik, false);
        const houseAccountData = houseAccountTaxCalculation(formik.values.recipients, summary);
        return formik.setValues((v) => ({...v, ...houseAccountData}));
      }

      // Capture the loaded tax before any Folio calculation or restore can bypass it.
      if (module === "edit-order" && taxLines(formik.values).length > 0) {
        const basis = orderTaxSignature(formik.values);
        if (!savedTax.current || savedTax.current.loaded !== formik.values.order_tax_signature) savedTax.current = {
          loaded: formik.values.order_tax_signature,
          basis,
          order: pick(formik.values, ORDER_TAX),
          lines: Object.fromEntries(taxLines(formik.values).map((p) => [p.product_key, pick(p, LINE_TAX)])),
        };
        if (savedTax.current.basis === basis) {
          lastEstimateReq.current = null;
          return restoreTax(savedTax.current);
        }
      }

      const orderedDate = moment(orderDetail?.createdAt).format("YYYY-MM-DD HH:mm");
      const startDateKintsugiTax = ALLOW_KINTSUGI_TAX_DATE;
      const duplicateOrder =
        formik.values.order_type_identifier === 2 ? true : false;
      const pickupOrder =
        formik.values.delivery_information.order_type === 1 ? true : false;
      const isValidZipcode =
        formik.values.delivery_information?.zipcode?.length === 5 &&
        formik.values.delivery_information?.zipcode !== "";
      const isValidOrderDate =
        (!duplicateOrder && orderedDate > startDateKintsugiTax) ||
        duplicateOrder;

      if (
        ((isValidZipcode && !pickupOrder) || pickupOrder) &&
        summary.total_qty > 0 &&
        isValidOrderDate &&
        !formik.values.is_replace_order &&
        formik.values.payment_type !== "house_account"
      ) {
        const summary = calcuateSummary(formik, false);
        const values = { ...formik.values, recipients: calculateKintsugiItemPrice(summary, formik) };
        const user_id = await getUser();
        const order = orderObject(values, summary, user_id);
        const postBody = {
          order: order,
        };
        const estimateSig = JSON.stringify(postBody);
        if (estimateSig === lastEstimateReq.current) {
          return;
        }
        lastEstimateReq.current = estimateSig;
        inflight.current += 1;
        setEstimating(true);
        try {
          const estimatedTaxRes = await getEstimateTax(postBody);
          // A newer request or a restore owns the tax now: a late answer must not overwrite it.
          if (lastEstimateReq.current !== estimateSig) return;
          if (estimatedTaxRes && estimatedTaxRes?.status === 200) {
            await updatedEstmatedResToRecData(estimatedTaxRes.data.data.data);
          } else {
            showToastMessage(
              undefined,
              estimatedTaxRes.data.message || "Error in tax calculation",
              "error",
              false
            );
          }
        } finally {
          if (!--inflight.current) setEstimating(false);
        }
      }
    } catch (error) {
      showToastMessage(
        undefined,
        error.message || "Error in tax calculation",
        "error",
        false
      );
    }
  }, [
    summary.sub_total,
    formik.values.delivery_information.delivery_type,
    formik.values.delivery_information.zipcode,
    formik.values.discount.discount_percentage,
    formik.values.discount.discount_price,
    summary.total_qty,
    formik.values.delivery_information.storeId,
    formik.values.delivery_information.charge,
    formik.values.tax_id,
    formik.values.payment_type,
    summary.discount_price,
    formik.values.tax_amount,
    formik.values.order_tax_signature,
    isQuickSendOrder,
  ]);

  const updatedEstmatedResToRecData = async (data) => {
    const { order } = data;
    const {
      receipient,
      tax,
      is_tax_calculated_manually,
      tax_rate,
      taxable_amount,
    } = order[0];
    const taxLines = receipient.flatMap((r) => r.recipient_product);
    const taxBase = taxLines.reduce((sum, p) => sum + Number(p.kintsugi_item_price || 0), 0);
    const folioTaxRate = taxBase > 0 ? taxLines.reduce((sum, p) => sum + Number(p.tax || 0), 0) / taxBase : 0;
    const priced = calcuateSummary({ values: { ...formik.values, tax_amount: +tax, folio_tax_rate: folioTaxRate } }, !isQuickSendOrder);
    const bonusChange = priced.folio_bonus_discount - calcuateSummary(formik, false).folio_bonus_discount;
    let taxableReduction = 0;
    if (bonusChange) {
      let taxReduction = 0;
      const taxedLine = taxLines.reduce((largest, p) => Number(p.tax) > Number(largest?.tax || 0) ? p : largest, null);
      taxLines.forEach((p) => {
        const base = Number(p.kintsugi_item_price) || 0;
        const discount = bonusChange * Number(p.total_price) / priced.sub_total;
        const ratio = base > 0 ? Math.max(0, 1 - discount / base) : 0;
        const lineTax = +(Number(p.tax) * ratio).toFixed(2);
        const lineTaxable = +(Number(p.taxable_amount) * ratio).toFixed(2);
        taxReduction += Number(p.tax) - lineTax;
        taxableReduction += Number(p.taxable_amount) - lineTaxable;
        Object.assign(p, { tax: lineTax, taxable_amount: lineTaxable, kintsugi_item_price: base > 0 ? +(base - discount).toFixed(4) : base });
      });
      if (taxedLine) taxedLine.tax = +(taxedLine.tax + priced.tax - Number(tax) + taxReduction).toFixed(2);
    }
    //update the tax amount and required related data to the recipient data
    const recipientsData = formik.values.recipients?.map((recipient) => {
      let recProductIndex = receipient.findIndex(
        (r) => r?.recipient_key === recipient?.recipient_key
      );
      const lines = { 0: recipient.products, 1: recipient.addons };
      receipient[recProductIndex]?.recipient_product?.forEach((product) => {
        const line = lines[product?.product_type];
        const productIndex = line?.findIndex(
          (p) => p?.product_key === product?.product_key
        ) ?? -1;
        if (productIndex !== -1) {
          Object.assign(line[productIndex], {
            taxable_amount: +product?.taxable_amount || 0,
            tax_rate: +product.tax_rate || 0,
            tax: +product?.tax || 0,
            kintsugi_product_id: product?.kintsugi_product_id || "",
            kintsugi_item_price: +product?.kintsugi_item_price || 0,
          });
        }
      });
      return recipient;
    });

    formik.setValues((v) => {
      return {
        ...v,
        is_tax_calculated_manually: is_tax_calculated_manually,
        taxable_amount: +(Number(taxable_amount) - taxableReduction).toFixed(2),
        recipients: recipientsData,
        tax_rate: +tax_rate,
        tax_amount: +tax,
        folio_tax_rate: folioTaxRate,
      };
    });
  };

  //Reset the tax related data when payment_type = "house_account".
  const resetTaxCalRelatedData = useCallback(() => {
    if (summary.total_qty < 1 && formik.values.total_product_qty < 1) {
      const zero = (p) => ({ ...p, ...Object.fromEntries(LINE_TAX.map((k) => [k, 0])) });
      formik.setValues((v) => ({
        ...v,
        is_tax_calculated_manually: false,
        tax_rate: 0,
        tax_amount: 0,
        house_account_tax_amount: 0,
        taxable_amount: 0,
        recipients: v.recipients.map((r) => ({ ...r, products: r.products.map(zero), addons: r.addons.map(zero) })),
      }));
    }
  }, [summary.total_qty < 1]);

  useEffect(() => {
    taxCalculation();
  }, [taxCalculation]);

  useEffect(() => {
    resetTaxCalRelatedData();
  }, [resetTaxCalRelatedData]);

  //Editing the card message, card signature, recipient address, etc. all
  //produce a fresh `recipients` array reference on every keystroke. The order
  //summary only depends on product/addon pricing and quantity, so derive a
  //stable signature of just those fields and drive the summary effect off it.
  //Depending on the whole `recipients` reference made the effect (and its
  //cascade of re-renders) run on every card-message keystroke.
  const recipientsSummarySignature = useMemo(
    () =>
      JSON.stringify(
        (formik.values.recipients || []).map((recipient) => ({
          products: (recipient?.products || []).map((product) => ({
            total_price: product?.total_price,
            qty: product?.qty,
            commission: product?.selectedVariant?.commission,
          })),
          addons: (recipient?.addons || []).map((addon) => ({
            total_price: addon?.total_price,
            qty: addon?.qty,
            commission: addon?.selectedVariant?.commission,
          })),
        }))
      ),
    [formik.values.recipients]
  );

  useEffect(() => {
    const summ = calcuateSummary(formik, !isQuickSendOrder);
    // 2Tap edit keeps the charged total: keep its delivery charge, tax takes the rest; isQuickSendOrder is not a dep (it would clobber loaded values).
    const chargedDelivery = Number(orderDetail?.delivery_charges || 0);
    const keptTotal = Number(formik.values.mainTotal);
    const keptTax = keptTotal - summ.total + Number(summ.tax) + Number(summ.delivery_charges || 0) - chargedDelivery;
    setSummary(isQuickSendOrder ? { ...summ, delivery_charges: chargedDelivery, tax: keptTax, total: keptTotal, add_fund: 0, refund: 0 } : summ);
    // setFieldValue patches the latest state; setValues here would overwrite a tax restore from this same flush.
    formik.setFieldValue("total_product_qty", summ.total_qty, false);
  }, [
    recipientsSummarySignature,
    formik.values.delivery_information.charge,
    formik.values.delivery_information.upcharge_amount,
    formik.values.discount,
    formik.values.tax_id,
    formik.values.payment_type,
    formik.values.taxable_amount,
    formik.values.tax_amount,
    formik.values.folio_balance,
    formik.values.folio_tax_rate,
  ]);

  let delivery_charge =
    summary.delivery_charges === 0
      ? 0
      : +summary.delivery_charges;

  //check if the tax amount and delivery charge amount is same as the order detail tax and delivery charge amount
  const isDeliveryChargeDeff = checkPriceDefference(delivery_charge, orderDetail?.delivery_charges);
  const isTaxDeff = checkPriceDefference(summary.tax, orderDetail?.tax);
  const isUpchargeDeff = checkPriceDefference(summary.upcharge_amount, orderDetail?.upcharge_amount);

  const paymentType = formik.values.payment_type;
  const replacementOrder = formik.values.order_type_identifier === 1;

  return (
    <div>
      <div className={`${styles.subTotalBlock}`}>
        {/* subtotal */}
        <div className={`${styles.totalBlockItem} mb-2`}>
          <span>Sub Total</span>
          <span>${summary.sub_total.toFixed(2)}</span>
        </div>
        {/* discount */}
        { !replacementOrder && (
          <div
            className={`${styles.totalBlockItem} align-items-center mb-2`}
            style={{ position: "relative" }}
          >
            <span>
              Discount
              <span
                className={`btn-sm outline-btn ml-3 cursor-pointer ${styles.totalBlockBtn}`}
                onClick={() => setShowDiscountModal(active === 0 ? true : false)}
              >
                + Discount
              </span>
              <br></br>
              {formik.values?.discount?.discount_promocode && (
                <span
                  style={{
                    fontSize: "13px",
                    position: "absolute",
                    bottom: "-10px",
                    margin: 0,
                    padding: 0,
                    lineHeight: 1,
                  }}
                >
                  {"(Promo Code : " +
                    formik.values?.discount?.discount_promocode +
                    ")"}
                </span>
              )}
            </span>
            <span className={`${styles.borderBottom}`}>
              - ${parseFloat(summary.discount_price).toFixed(2)}
            </span>
          </div>  
        )}
        {/* Replacement Discount */}
        {replacementOrder && (
          <div
            className={`${styles.totalBlockItem} align-items-center mb-2`}
            style={{ position: "relative" }}
          >
            <span>
              Replacement Discount
              <br></br>
            </span>
            <span className={`${styles.borderBottom}`}>
                - ${parseFloat(summary.replacement_discount_amount).toFixed(2)}
            </span>
          </div>
        )}
        {/* Subscription discount */}
        {orderDetail?.is_subscribe_order && (
          <div className={`${styles.totalBlockItem} align-items-center mb-2`}>
            <span>Subscription Discount</span>
            <span className={`${styles.borderBottom}`}>
              - ${summary.subscription_discount}
            </span>
          </div>
        )}

        {/* // redeem amount */}
        {module === "edit-order" && (
          <div className={`${styles.totalBlockItem} align-items-center mb-2`}>
            <span>User Redeem Amount</span>
            <span>
              $
              {formik.values.petal_reward.petal_rewards_point_amount.toFixed(2)}
            </span>
          </div>
        )}

        {summary.folio_bonus_discount > 0 && (
          <div className={`${styles.totalBlockItem} align-items-center mb-2`}>
            <span>Flower Folio bonus</span><span>-${summary.folio_bonus_discount.toFixed(2)}</span>
          </div>
        )}
        {/* tax */}
        <div className={`${styles.totalBlockItem} align-items-center mb-2`}>
          <span>
            Tax
            {paymentType === "house_account" ? (
              <span
                className={`btn-sm outline-btn ml-3 cursor-pointer ${styles.totalBlockBtn}`}
                onClick={() => setShowTax(active === 0 ? true : false)}
              >
                + Add Tax
              </span>
            ):  
              <span
                  className={`btn-sm outline-btn ml-3 cursor-pointer ${styles.totalBlockBtn}`}
                  onClick={() => setShowTaxModal(active === 0 ? true : false)}
                >
                + Tax ID
              </span>
            }
          </span>
          <span>
            {module === "edit-order" && !isTaxDeff &&(
              <small style={{ color: "#F37C7C", fontSize:14 }}> 
                {`(${parseFloat(orderDetail?.tax || 0).toFixed(2)}) `}
              </small>
            )} 
            ${summary?.tax?.toFixed(2)}
          </span>
        </div>
        {/* delivery charge */}
        <div className={`${styles.totalBlockItem} mb-2`}>
          <span>
            Service/Delivery
            {
              props.module === 'edit-order' && formik.values.delivery_information.order_type === 0 ? <span
                className={`btn-sm outline-btn ml-3 cursor-pointer ${styles.totalBlockBtn}`}
                onClick={() => setShowServiceModel(true)}
              >
                Service/Delivery
              </span> : null
            }

          </span>
          <span>
            {module === "edit-order" && !isDeliveryChargeDeff && (
              <small style={{ color: "#F37C7C", fontSize:14 }}> 
                {`(${parseFloat(orderDetail?.delivery_charges || 0).toFixed(2)}) `}
              </small>
            )}
            ${parseFloat(delivery_charge).toFixed(2)}
          </span>
        </div>
          {/* Upcharge */}
        <div className={`${styles.totalBlockItem} mb-2`}>
          <span>
          Upcharge
            {
              props.module === 'edit-order' && formik.values.delivery_information.order_type === 0 ? <span
                className={`btn-sm outline-btn ml-3 cursor-pointer ${styles.totalBlockBtn}`}
                onClick={() => setShowUpcharge(true)}
              >
                Upcharge
              </span> : null
            }

          </span>
          <span>
            {module === "edit-order" && !isUpchargeDeff && (
              <small style={{ color: "#F37C7C", fontSize:14 }}> 
                {`(${parseFloat(orderDetail.upcharge_amount || 0).toFixed(2)}) `}
              </small>
            )}
            ${parseFloat(summary.upcharge_amount).toFixed(2)}
          </span>
        </div>

        {summary.folio_edit_amount > 0 && (
          <div className={`${styles.totalBlockItem} mb-2`}>
            <span>Additional Flower Folio payment</span>
            <span>-${(summary.folio_edit_amount - summary.folio_edit_bonus).toFixed(2)}</span>
          </div>
        )}

        {/* add fund and refund */}
        {module === "edit-order" && (
          <>
            <div className={`${styles.totalBlockItem} align-items-center mb-2`}>
              <span>
                Add Funds
              </span>
              <span>${parseFloat(summary.add_fund).toFixed(2)}</span>
            </div>
            <div className={`${styles.totalBlockItem} align-items-center mb-2`}>
              <span>
                Refunds
              </span>
              <span>-${parseFloat(summary.refund).toFixed(2)}</span>
            </div>
          </>
        )}
      </div>
      {/* total */}
      <div className={`${styles.totalBlock} mt-2`}>
        <div className={`${styles.mainTotalBlockItem} mb-2`}>
          <span>Total</span>
          <span>${summary.total.toFixed(2)}</span>
        </div>
        <div className={`${styles.totalBlockItem} align-items-center mb-2`}>
          <span style={{ fontSize: "14px" }}>
            (This amount is the total cart amount. Additional Add fund is not
            considered in this total).
          </span>
        </div>
        {/* General info of amount */}
        {module === "edit-order" && (
          <>
            <div
              className={`${styles.totalBlockItem} align-items-center mb-2`}
              style={{ fontSize: "14px" }}
            >
              <span>Amount recieved till now</span>
              <span>${formik.values.total_fund.toFixed(2)}</span>
            </div>
            <div
              className={`${styles.totalBlockItem} align-items-center mb-2`}
              style={{ fontSize: "14px" }}
            >
              <span>Amount refunded till now</span>
              <span>-${formik.values.total_refund.toFixed(2)}</span>
            </div>
          </>
        )}
        {/* discount reason */}
        {formik.values.discount.discount_reason && (
          <div>
            <div className={`${styles.totalBlockItem} mb-2`}>
              <span>Discount Reason</span>
            </div>
            <div className={`${styles.totalBlockItem} mb-2`}>
              <span>{formik.values.discount.discount_reason}</span>
            </div>
          </div>
        )}
      </div>

      <DeliveryAndService
        showModal={showServiceModel}
        pageTitle="Service/Delivery"
        serviceChargeData={delivery_charge ? parseFloat(delivery_charge).toFixed(2) : 0}
        handleCancelClick={(e) => {
          setShowServiceModel(false);
        }}
        handleSuccess={(charge) => {
          setShowServiceModel(false);
        }}
      />

      <UpchargeModel
        showModal={showUpcharge}
        pageTitle="Upcharge"
        upchargeValue={summary.upcharge_amount ? parseFloat(summary.upcharge_amount).toFixed(2) : 0}
        handleCancelClick={(e) => {
          setShowUpcharge(false);
        }}
        handleClick={() => {
          setShowUpcharge(false);
        }}
        id={"upcharge"}
      />

      <DiscountModal
        showModal={showDiscountModal}
        pageTitle="Add Discount"
        submitStatus={false}
        handleCancelClick={(e) => {
          setShowDiscountModal(false);
        }}
      />
      <TaxModal
        showModal={showTaxModal}
        pageTitle="Add Tax ID"
        handleCancelClick={(e) => {
          setShowTaxModal(false);
        }}
      />
      <Tax
        showModal={showTax}
        pageTitle="Add Tax"
        submitStatus={false}
        handleCancelClick={(e) => {
          setShowTax(false);
        }}
      />
    </div>
  );
};
export default TotalSummary;
